import { FC, ReactNode, FormEvent, useState } from 'react';

import { useSession } from '@eneco/auth';
import Logger from '@eneco/common/log';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackViewComponentWithScopeAndVariant } from '@eneco/components/TrackViewComponentWithScopeAndVariant/TrackViewComponentWithScopeAndVariant';
import { useFormatter } from '@eneco/i18n';
import { useRouter } from '@eneco/next';
import { Box, Button, Heading, RadioButton, RadioGroup, Stack } from '@eneco/sparky';
import { SensitiveContentComponent } from '@eneco/tracking';
import { Customers_Address_AddressViewModel, Customers_Profile_AccountResponseModel } from '@monorepo-types/dc';

import { getAccountSwitchHref } from './accountSwitchHelpers';

interface Props {
  heading: string;
  subheading: string;
  content: string;
  submit: string | null;
  accounts: Array<Customers_Profile_AccountResponseModel>;
  selectedAccountId: number;
  customerId: number;
  trigger: ReactNode;
  onAccountSwitch?: (accountId: number) => void;
}

const AccountSwitchDialog: FC<Props> = ({
  heading,
  subheading,
  content,
  submit,
  customerId,
  accounts,
  selectedAccountId,
  trigger,
  onAccountSwitch,
}) => {
  const [isNavigating, setIsNavigating] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(selectedAccountId.toString());
  const { data: session } = useSession() ?? {};
  const { address: addressFormatter } = useFormatter();
  const { reload } = useRouter();

  const createAccountLabel = (customerId: number, accountId: number, address?: Customers_Address_AddressViewModel) => {
    if (address) {
      return `${addressFormatter.medium(address)}, ${customerId}-${accountId}`;
    }
    return accountId.toString();
  };

  const labeledAccounts = accounts.map(account => {
    return {
      ...account,
      accountLabel: createAccountLabel(customerId, account.accountId, account.address),
    };
  });

  const changeAccount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsNavigating(true);
    try {
      const formData = new FormData();
      formData.append('accountId', selectedAccount);

      const response = await fetch(getAccountSwitchHref(selectedAccount), {
        method: 'POST',
        headers: { authorization: `Bearer ${session?.idToken}` },
        body: formData,
      });

      if (response.ok) {
        onAccountSwitch?.(Number(selectedAccount));
        reload();
      } else {
        Logger.error('FsXDTS', `Account switch failed with status: ${response.status}`);
      }
    } catch (error) {
      Logger.error('FsXDTS', `Unexpected error for switching accounts`, error);
    } finally {
      setIsNavigating(false);
    }
  };

  const hasSubheading = !!subheading;

  return (
    <TrackViewComponentWithScopeAndVariant
      scope="aansluitadreswidget"
      type={'dialog'}
      label="Aansluitadres"
      variant={'Geselecteerd aansluitadres'}>
      <TrackedDialog trigger={trigger}>
        <Stack gap="6">
          <Stack>
            <Heading as="h3" size="XS" id={hasSubheading ? 'account-label' : undefined}>
              {heading}
            </Heading>
            <Box paddingTop="1">
              <RichText html={content} />
            </Box>
          </Stack>
          <Stack gap="3">
            {hasSubheading ? (
              <Heading as="h4" size="3XS" id="account-label">
                {subheading}
              </Heading>
            ) : null}
            <form noValidate onSubmit={changeAccount}>
              <Stack gap="6">
                <RadioGroup
                  name="account"
                  aria-labelledby="account-label"
                  direction="column"
                  value={selectedAccount}
                  onValueChange={setSelectedAccount}>
                  {labeledAccounts.map((account, index) => (
                    <SensitiveContentComponent
                      contentMask={`masked account address ${index + 1}`}
                      key={account.accountLabel}>
                      <RadioButton value={account.accountId.toString()}>{account.accountLabel}</RadioButton>
                    </SensitiveContentComponent>
                  ))}
                </RadioGroup>
                <Stack.Item>
                  <Button type="submit" isDisabled={isNavigating} action="primary">
                    {submit}
                  </Button>
                </Stack.Item>
              </Stack>
            </form>
          </Stack>
        </Stack>
      </TrackedDialog>
    </TrackViewComponentWithScopeAndVariant>
  );
};

export default AccountSwitchDialog;
